import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { messageFrom, TOKEN_KEY } from '../lib/account';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";

// The page behind the "Confirm this email" link. The change only happens when the person taps the button (email scanners that
// pre-open links can't confirm it for them).
const ConfirmEmail = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const confirm = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/confirm-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        try { localStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ }     // every session was signed out
        setDone(body.email || true);
      } else if (res.status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else {
        setError(messageFrom(body, 'Something went wrong. Please request the change again.'));
      }
    } catch (e) {
      setError('We could not reach RideBuraq. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center px-5">
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-orange-600/15 blur-[120px]" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={LOGO} alt="RideBuraq" className="w-14 h-14 object-contain mb-5" />
          <p className="text-[11px] tracking-[0.3em] uppercase text-orange-400/80 mb-2">Account help</p>
          <h1 className="text-4xl font-semibold tracking-tight">Confirm your new email</h1>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 text-center space-y-3">
          {!token ? (
            <>
              <p data-testid="confirm-no-token" className="text-lg font-medium">This link is not complete.</p>
              <p className="text-sm text-gray-400">Open the link from your email again, or request the change again from your profile.</p>
            </>
          ) : done ? (
            <>
              <p data-testid="confirm-done" className="text-lg font-medium">Your email has been changed.</p>
              <p className="text-sm text-gray-400">You were signed out everywhere. Sign in with your new email{typeof done === 'string' ? ` (${done})` : ''}.</p>
              <button onClick={() => navigate('/login')} className="w-full rounded-full py-3.5 mt-2 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600">Go to sign in</button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-400">Tap the button to make this your RideBuraq email. You will be signed out everywhere and sign in again with it.</p>
              {error && <p data-testid="confirm-error" className="text-sm text-red-400">{error}</p>}
              <button data-testid="confirm-submit" onClick={confirm} disabled={loading} className="w-full rounded-full py-3.5 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 disabled:opacity-60 flex items-center justify-center">
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Confirming...</>) : 'Confirm this email'}
              </button>
              <p className="text-xs text-gray-500 pt-1">Didn't ask for this? Just close this page: nothing changes. <Link to="/login" className="text-orange-400">Sign in</Link></p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmEmail;
